/**
 * @file DashboardPage.tsx
 * @description Authenticated landing surface. Role-split:
 *   - admin/staff → CustomersList (manage customers; projects nest inside)
 *   - customer    → their projects
 *   Server enforces ownership on every API call; this is UX routing only.
 * @module Pages
 * @security Mounted under ProtectedRoute that redirects to /login when
 *   the access token is null.
 */

import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { AppLayout } from '../components/common/AppLayout';
import { CustomersList } from '../components/customers/CustomersList';
import { CustomerGreeting } from '../components/dashboard/CustomerGreeting';
import { CustomerProjectsList } from '../components/dashboard/CustomerProjectsList';
import { NextDeadlineCallout } from '../components/dashboard/NextDeadlineCallout';
import { SummaryChips } from '../components/dashboard/SummaryChips';
import { WelcomeCard } from '../components/dashboard/WelcomeCard';
import { listProjects, type ProjectDto } from '../lib/projects-api';
import { useAuthStore } from '../stores/auth.store';

export default function DashboardPage(): JSX.Element {
  const role = useAuthStore((s) => s.claims?.role);
  return (
    <AppLayout>
      {role === 'admin' || role === 'staff' ? <CustomersList /> : <CustomerProjectsView />}
    </AppLayout>
  );
}

/**
 * Customer's own dashboard — personalized greeting + their projects.
 * They don't create projects (admin opens them), but they see, edit,
 * submit, accept quotes, etc. Status pills + action badges call out
 * which projects need their input now.
 */
function CustomerProjectsView(): JSX.Element {
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listProjects({ includeArchived })
      .then((res) => {
        if (!cancelled) setProjects(res.projects);
      })
      .catch(() => {
        if (!cancelled) setProjects([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [includeArchived]);

  return (
    <>
      <CustomerGreeting />
      <WelcomeCard />
      <SummaryChips />
      <NextDeadlineCallout projects={projects} />
      <CustomerProjectsList
        projects={projects}
        loading={loading}
        includeArchived={includeArchived}
        onToggleArchived={setIncludeArchived}
      />
    </>
  );
}
